import { FormikProps, FormikTouched } from "formik";
import ValidationErrorMessage from "./ValidationErrorMessage";
import { ChangeEvent, forwardRef, useEffect, useRef } from "react";
import IsRequired from "./common/IsRequired";

interface InputCheckBoxProps {
  label: string;
  className?: string;
  id: string;
  checked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  errorMessage?: string;
  touched?: boolean | FormikTouched<any> | FormikTouched<any>[] | undefined;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  isRequired?: boolean;
}

const InputCheckbox = forwardRef<HTMLInputElement, InputCheckBoxProps>(
  (props, ref) => {
    const {
      label,
      className,
      id,
      checked,
      defaultChecked,
      disabled,
      touched,
      errorMessage,
      onChange,
      isRequired,
    } = props;
    return (
      <div className={`form-check${className ? ` ${className}` : ""}`}>
        <input
          className={`form-check-input${
            touched && errorMessage ? " is-invalid" : ""
          }`}
          type="checkbox"
          id={id}
          ref={ref}
          checked={checked}
          defaultChecked={defaultChecked}
          disabled={disabled}
          onChange={onChange}
        />
        <label className="form-check-label" htmlFor={id}>
          {label}
          {isRequired && <IsRequired />}
        </label>
      </div>
    );
  },
);

export function BooleanCheckboxFormControl<U>(props: {
  config: ICheckBoxEdit<U>;
  formik: FormikProps<any>;
  disabled?: boolean;
}) {
  const { config, formik, disabled } = props;
  const { setFieldValue, values } = formik;
  const { id, label, isRequired } = config;

  return (
    <>
      {label && (
        <label className="form-check-label" htmlFor={id}>
          {label}
          {isRequired && <IsRequired />}
        </label>
      )}
      <div className="my-1">
        <InputCheckbox
          className={
            formik.touched[id] && (formik as any).errors[id]
              ? " is-invalid"
              : ""
          }
          disabled={disabled}
          label={config.inputLabel}
          id={id}
          checked={values[id] ? true : false}
          touched={formik.touched[id]}
          errorMessage={(formik as any).errors[id]}
          onChange={(e) => {
            setFieldValue(id, e.target.checked, true);
          }}
        />
      </div>
      <ValidationErrorMessage
        touched={formik.touched[id]}
        errorMessage={(formik as any).errors[id]}
      />
    </>
  );
}

export default InputCheckbox;

export type CHECKBOX_STATE = "CHECKED" | "INDETERMINATE" | "EMPTY";
interface ITriStateCheckboxProps extends React.DetailedHTMLProps<
  React.InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
> {
  checkboxState: CHECKBOX_STATE;
}

/**
 * input type="checkbox" that accepts checkboxState that can be one
 * of  "CHECKED" | "INDETERMINATE" | "EMPTY".
 */
export function Checkbox(props: ITriStateCheckboxProps) {
  const { checkboxState, ...rest } = props;
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) {
      switch (checkboxState) {
        case "CHECKED": {
          ref.current.checked = true;
          ref.current.indeterminate = false;
          break;
        }
        case "EMPTY": {
          ref.current.checked = false;
          ref.current.indeterminate = false;
          break;
        }
        case "INDETERMINATE": {
          ref.current.checked = false;
          ref.current.indeterminate = true;
          break;
        }
      }
    }
  }, [checkboxState]);
  return <input ref={ref} type="checkbox" {...rest} />;
}
